<template>
  <div class="max-w-7xl mx-auto pb-10 relative">

    <div class="space-y-6 transition-all duration-300 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showModal }">

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Registro de Garantías</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Hipotecas, prendas y fiadores asociados a los créditos.</p>
        </div>
        <button @click="openCreate" class="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95">
          <Plus class="w-5 h-5" /> Nueva Garantía
        </button>
      </div>

      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input v-model="searchTerm" type="text" placeholder="Buscar por cliente, folio o descripción..." class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all">
        </div>
        <select v-model="typeFilter" class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none">
          <option value="">Todos los Tipos</option>
          <option value="HIPOTECARIA">Hipotecaria</option>
          <option value="PRENDARIA">Prendaria</option>
          <option value="FIDUCIARIA">Fiduciaria</option>
        </select>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Crédito Asociado</th>
                <th class="px-6 py-4 font-semibold">Tipo y Detalle</th>
                <th class="px-6 py-4 font-semibold text-right">Monto Garantía</th>
                <th class="px-6 py-4 font-semibold text-right">Disponible</th>
                <th class="px-6 py-4 font-semibold text-center">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr v-if="list.length === 0"><td colspan="5" class="px-6 py-8 text-center text-slate-500">No hay garantías registradas.</td></tr>

              <tr v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-900 dark:text-slate-100">{{ item.customer_name }}</div>
                  <div class="text-[10px] font-mono text-slate-900 dark:text-slate-100">EXP: {{ item.loan_code }}</div>
                </td>
                <td class="px-6 py-4">
                  <span class="inline-flex px-2 py-0.5 rounded text-[10px] font-bold tracking-wide" :class="getTypeBadge(item.collateral_type)">
                    {{ item.collateral_type }}
                  </span>
                  <div class="text-xs text-slate-500 mt-1 max-w-[200px] truncate" :title="item.description || item.guarantor_name">
                    {{ item.collateral_type === 'FIDUCIARIA' ? 'Fiador: ' + item.guarantor_name : item.description }}
                  </div>
                </td>
                <td class="px-6 py-4 text-right font-medium text-slate-700  dark:text-slate-100">
                  ${{ Number(item.guarantee_amount).toLocaleString() }}
                </td>
                <td class="px-6 py-4 text-right">
                  <div class="font-bold text-brand-600  dark:text-slate-100">${{ Number(item.available_amount).toLocaleString() }}</div>
                  <div class="text-[9px] text-slate-400">Utilizado: ${{ Number(item.used_amount).toLocaleString() }}</div>
                </td>
                <td class="px-6 py-4 text-center">
                  <button class="p-2 text-slate-400 hover:text-white hover:bg-brand-50 rounded-lg"><FileText class="w-6 h-6" /></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <CollateralForm :show="showModal" @close="showModal = false" @saved="loadData" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Search, Plus, FileText } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import CollateralForm from './Form.vue';

const list = ref([]);
const showModal = ref(false);
const searchTerm = ref('');
const typeFilter = ref('');

const loadData = async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user?.company) return;
  const res = await apiClient.get(`/collaterals?company_id=${user.company}`);
  list.value = res.data.data || res.data || [];
};

onMounted(loadData);

const filteredList = computed(() => {
  let result = list.value;
  if (typeFilter.value) result = result.filter(i => i.collateral_type === typeFilter.value);
  if (searchTerm.value) {
    const q = searchTerm.value.toLowerCase();
    result = result.filter(i => i.customer_name?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q));
  }
  return result;
});

const getTypeBadge = (type) => {
  const map = {
    'HIPOTECARIA': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30',
    'PRENDARIA': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30',
    'FIDUCIARIA': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30'
  };
  return map[type] || 'bg-slate-100 text-slate-700';
};

const openCreate = () => { showModal.value = true; };
</script>